import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { id: "check-in", label: "Check-in" },
  { id: "wash", label: "Wash & Prep" },
  { id: "treatment", label: "Treatment" },
  { id: "quality-check", label: "Quality Check" },
  { id: "delivered", label: "Delivered" },
];

export default function Track() {
  const { jobId: urlJobId } = useParams();
  const [params] = useSearchParams();
  const [jobId, setJobId] = useState(urlJobId || params.get("jobId") || "");
  const [job, setJob] = useState(null);

  const load = async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/jobs/track/${id}`);
      setJob(res.data);
    } catch { toast.error("Job not found"); setJob(null); }
  };

  useEffect(() => { if (urlJobId) load(urlJobId); }, [urlJobId]);

  const submit = (e) => { e.preventDefault(); load(jobId); };
  const currentIdx = job ? STAGES.findIndex((s) => s.id === job.stage) : -1;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="track-page">
      <div className="overline text-[#FFB800]">Live Job Status</div>
      <h1 className="font-display text-4xl mt-2">Track your vehicle.</h1>
      <form onSubmit={submit} className="mt-8 flex gap-3" data-testid="track-form">
        <Input data-testid="track-input" value={jobId} onChange={(e) => setJobId(e.target.value)}
          placeholder="Enter Job ID (e.g. J-A1B2C3D4)" className="bg-[#0A0A0C] border-zinc-800 h-11" />
        <button className="btn-primary" type="submit" data-testid="track-submit"><Search className="h-4 w-4" /> Track</button>
      </form>

      {job && (
        <div className="mt-10 card-surface rounded-lg p-8" data-testid="job-details">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="overline">Job</div>
              <div className="font-mono text-lg mt-1">{job.id}</div>
              <div className="text-sm text-zinc-400 mt-1">{job.car_make} {job.car_model} · {job.service}</div>
            </div>
            <div className="text-right">
              <div className="overline">Customer</div>
              <div className="text-sm mt-1">{job.customer_name}</div>
            </div>
          </div>

          <div className="mt-10 space-y-3">
            {STAGES.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.id} data-testid={`stage-${s.id}`}
                  className={`flex items-center gap-4 border rounded-md px-4 py-3 ${active ? "border-[#FFB800] stage-active" : done ? "border-zinc-700" : "border-zinc-900"}`}>
                  {done ? <CheckCircle2 className="h-5 w-5 text-[#FFB800]" /> : <Circle className="h-5 w-5 text-zinc-700" />}
                  <div className="flex-1">
                    <div className="font-mono text-xs text-zinc-500">STAGE {i + 1}</div>
                    <div className="text-sm">{s.label}</div>
                  </div>
                  {active && <span className="text-xs text-[#FFB800] uppercase tracking-wider">In progress</span>}
                </div>
              );
            })}
          </div>

          {job.history?.length > 0 && (
            <div className="mt-10">
              <div className="overline mb-3">Timeline</div>
              <ul className="space-y-2 text-sm">
                {job.history.map((h, i) => (
                  <li key={i} className="flex justify-between text-zinc-400">
                    <span>{h.stage}{h.note ? ` — ${h.note}` : ""}</span>
                    <span className="font-mono text-xs">{new Date(h.at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
